import { Controller } from "@hotwired/stimulus"

// Interactive guide controller for all assistant edit tabs.
// Uses driver.js to walk users through each tab's features.
// Attaches to the guide restart button area; reads page_args for paths/tab.
export default class extends Controller {
  connect() {
    this.initProfileTabGuide()
    this.initKnowledgeBaseTabGuide()
    this.initHumanHandoverTabGuide()
    this.initIntakeFormTabGuide()
    this.initInstructionsTabGuide()
    this.initAnalyticsTabGuide()
    this.initWidgetTabGuide()
    this.initTriggersTabGuide()
    this.initGuideRestartButton()
  }

  get guideUtils() {
    const self = this
    return {
      getHeaders() { return { 'Content-Type': 'application/json', 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'), 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } },
      fetchGuideState() {
        if (!page_args.interactive_guide_state_path) return Promise.reject(new Error('no path'))
        return fetch(page_args.interactive_guide_state_path, { method: 'GET', headers: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'), 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' } }).then(r => { if (!r.ok) throw new Error('Network error'); return r.json() })
      },
      saveStep(tabName, step) {
        if (!page_args.update_interactive_guide_path) return Promise.reject(new Error('no path'))
        return fetch(page_args.update_interactive_guide_path, { method: 'POST', headers: this.getHeaders(), body: JSON.stringify({ guide_type: 'tab_guide', tab_name: tabName, step }) })
      },
      markAsSkipped(tabName) {
        if (!page_args.update_interactive_guide_path) return Promise.reject(new Error('no path'))
        return fetch(page_args.update_interactive_guide_path, { method: 'POST', headers: this.getHeaders(), body: JSON.stringify({ guide_type: 'tab_guide', tab_name: tabName, skipped: true }) })
      },
      skipAllGuides() {
        if (!page_args.update_interactive_guide_path) return Promise.reject(new Error('no path'))
        return fetch(page_args.update_interactive_guide_path, { method: 'POST', headers: this.getHeaders(), body: JSON.stringify({ guide_type: 'skip_all' }) })
      },
      getDriverFn() { const fn = window.driver && window.driver.js && window.driver.js.driver; return (fn && typeof fn === 'function') ? fn : null }
    }
  }

  fixPopoverPosition(selector) {
    const el = document.querySelector(selector || '.driver-popover')
    if (!el) return
    const rect = el.getBoundingClientRect(), vw = window.innerWidth, minOffset = 45
    if (rect.left < minOffset) { el.style.left = minOffset + 'px'; el.style.transform = 'translateY(-50%)' }
    if (rect.right > vw - minOffset) el.style.left = (vw - rect.width - minOffset) + 'px'
  }

  createPopoverRenderer(getDriverObj, steps) {
    const self = this
    return function(popover, { config, state }) {
      setTimeout(function() {
        const driverObj = getDriverObj(); if (!driverObj) return
        const popoverElement = document.querySelector('.profile-tab-guide-popover'); if (!popoverElement) return
        self.fixPopoverPosition('.profile-tab-guide-popover')
        const activeIndex = driverObj.getActiveIndex()
        let footer = popoverElement.querySelector('.driver-popover-footer')
        if (!footer) { footer = document.createElement('div'); footer.className = 'driver-popover-footer'; popoverElement.appendChild(footer) }
        if (!footer.querySelector('.guide-skip-link')) {
          const skipLink = document.createElement('a'); skipLink.href = '#'; skipLink.className = 'guide-skip-link'; skipLink.textContent = 'Skip tour'
          skipLink.addEventListener('click', function(e) {
            e.preventDefault(); e.stopPropagation()
            self.guideUtils.skipAllGuides().then(() => { if (driverObj) driverObj.destroy(); setTimeout(() => { self.ensureRestartButton(); self.showGuideCompletionPopup() }, 300) })
          })
          footer.insertBefore(skipLink, footer.firstChild)
        }
        Object.assign(footer.style, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', backgroundColor: '#1C51C0', borderTop: 'none', gap: '12px' })
        const navBtns = footer.querySelector('.driver-popover-navigation-btns')
        if (navBtns) Object.assign(navBtns.style, { display: 'flex', gap: '8px', marginLeft: 'auto' })
        const prevBtn = popoverElement.querySelector('.driver-popover-prev-btn')
        if (prevBtn) { prevBtn.style.display = activeIndex === 0 ? 'none' : ''; if (activeIndex > 0) prevBtn.innerHTML = '‹ Previous' }
        const nextBtn = popoverElement.querySelector('.driver-popover-next-btn')
        if (nextBtn) nextBtn.innerHTML = activeIndex === steps.length - 1 ? 'Finish' : 'Next ›'
      }, 10)
    }
  }

  createTabGuide(config) {
    const self = this, gu = this.guideUtils
    const { tabName, maxSteps, getSteps, customHandlers = {}, customOptions = {} } = config
    return {
      init() {
        if (!page_args.interactive_guide_state_path || !page_args.update_interactive_guide_path) return
        if (page_args.tab_name !== tabName) return
        gu.fetchGuideState().then(data => {
          const guideSkipped = data.guide_skipped || false, tabProgress = (data.tab_guides_progress && data.tab_guides_progress[tabName]) || 0, tabSkipped = (data.tab_guides_skipped && data.tab_guides_skipped[tabName]) || false
          if (guideSkipped || tabSkipped || tabProgress === -1 || tabProgress >= maxSteps) return
          setTimeout(() => this.start(tabProgress), 500)
        }).catch(e => console.error('Failed to load guide state', e))
      },
      start(startStep) {
        const driverFn = gu.getDriverFn(); if (!driverFn) return
        self.ensureRestartButton()
        const steps = getSteps(); if (!steps || !steps.length) return
        let currentStep = startStep || 0, driverObj
        const getContext = () => ({ driverObj, currentStep, setCurrentStep(s) { currentStep = s }, steps, tabName })
        const wrappedOnNextClick = () => {
          if (customHandlers.onNextClick) return customHandlers.onNextClick(getContext())
          const next = driverObj.getActiveIndex() + 1
          if (next >= steps.length) { gu.saveStep(tabName, -1); driverObj.destroy() }
          else { currentStep = next; gu.saveStep(tabName, currentStep); driverObj.moveNext() }
        }
        const wrappedOnPrevClick = () => {
          if (customHandlers.onPrevClick) return customHandlers.onPrevClick(getContext())
          const prev = driverObj.getActiveIndex() - 1
          if (prev >= 0) { currentStep = prev; gu.saveStep(tabName, currentStep); driverObj.movePrevious() }
        }
        driverObj = driverFn(Object.assign({
          showProgress: false, showButtons: ['next', 'previous'], allowClose: false, disableActiveInteraction: true,
          nextBtnText: 'Next', prevBtnText: 'Previous', doneBtnText: 'Finish', overlayColor: 'rgba(0, 0, 0, 0.6)',
          stagePadding: 8, stageRadius: 8, popoverClass: 'profile-tab-guide-popover', steps,
          onNextClick: wrappedOnNextClick, onPrevClick: wrappedOnPrevClick,
          onPopoverRender: self.createPopoverRenderer(() => driverObj, steps),
          onDestroyed: customHandlers.onDestroyed || function() {},
          popoverOffset: 15
        }, customOptions))
        if (currentStep > 0 && currentStep < steps.length) driverObj.drive(currentStep)
        else driverObj.drive()
      }
    }
  }

  // ---- Tab guide definitions ----

  initProfileTabGuide() {
    this.createTabGuide({
      tabName: 'profile', maxSteps: 4,
      getSteps() {
        const profileTab = $('#tab-profile'), saveButton = $('#bot_submit'), kbTab = $('#tab-knowledge-base'), chatControl = $('#assistant-chat-control-group')
        if (!profileTab.length || !saveButton.length || !kbTab.length) return null
        const isAssistantAccount = page_args.assistant_account || false
        const steps = [{ element: profileTab[0], popover: { title: 'Profile tab', description: "Configure your agent's name and avatar here. These will be visible to users during chats.", side: 'bottom', align: 'start' } }]
        if (!isAssistantAccount && chatControl.length) steps.push({ element: chatControl[0], popover: { title: 'Agent Chat Settings', description: "This section explains how the Agent works in private and group chats.\n\nIn group chats, the Agent responds only when triggered by a command. Users can type /command to start interacting with the Agent, as long as it's part of the chat.", side: 'top', align: 'start' } })
        steps.push({ element: saveButton[0], popover: { title: 'Save Reminder', description: 'After making any changes, always remember to save them.', side: 'top', align: 'end' } })
        steps.push({ element: kbTab[0], popover: { title: 'Next Step', description: 'Use the Knowledge Base tab to add websites and documents that your agent can learn from.', side: 'bottom', align: 'start' } })
        return steps
      },
      customHandlers: {
        onNextClick(ctx) {
          const isAssistantAccount = page_args.assistant_account || false
          let next = ctx.driverObj.getActiveIndex() + 1
          if (isAssistantAccount && ctx.driverObj.getActiveIndex() === 0) { next += 1; if (next >= ctx.steps.length) next = ctx.steps.length - 1 }
          if (next >= ctx.steps.length) { ctx.driverObj.destroy(); return }
          ctx.setCurrentStep(next)
          const gu = { saveStep(tab, s) { return fetch(page_args.update_interactive_guide_path, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content'), 'X-Requested-With': 'XMLHttpRequest', 'Accept': 'application/json' }, body: JSON.stringify({ guide_type: 'tab_guide', tab_name: tab, step: s }) }) } }
          gu.saveStep('profile', next)
          ctx.driverObj.drive(next)
        }
      }
    }).init()
  }

  initKnowledgeBaseTabGuide() {
    if (!page_args.interactive_guide_state_path || !page_args.update_interactive_guide_path || page_args.tab_name !== 'knowledge_base') return
    const self = this, gu = this.guideUtils
    gu.fetchGuideState().then(data => {
      if (data.guide_skipped || (data.tab_guides_skipped && data.tab_guides_skipped.knowledge_base) || (data.tab_guides_progress && data.tab_guides_progress.knowledge_base) === -1 || (data.tab_guides_progress && data.tab_guides_progress.knowledge_base >= 5)) return
      const tabProgress = (data.tab_guides_progress && data.tab_guides_progress.knowledge_base) || 0
      setTimeout(() => self.startKnowledgeBaseGuide(tabProgress), 500)
    }).catch(e => console.error('Failed to load guide state', e))
  }

  startKnowledgeBaseGuide(startStep) {
    const driverFn = this.guideUtils.getDriverFn(); if (!driverFn) return
    const self = this, gu = this.guideUtils
    this.ensureRestartButton()
    let currentStep = startStep || 0, driverObj
    const saveStep = (step) => gu.saveStep('knowledge_base', step)
    const markAsSkipped = () => { gu.skipAllGuides().then(() => { if (driverObj) driverObj.destroy(); setTimeout(() => { self.ensureRestartButton(); self.showGuideCompletionPopup() }, 300) }) }
    const kbSection = $('.wrap_section .form-group:has(b:contains("AI Agent knowledge base"))').first()
    const myContent = $('#my-content-section').first(), unanswered = $('.form-group').has('b:contains("Unanswered questions")').first()
    const saveButton = $('#bot_submit'), hhTab = $('#tab-human-handover')
    if (!kbSection.length || !myContent.length || !unanswered.length || !saveButton.length || !hhTab.length) return
    const steps = [
      { element: kbSection[0], popover: { title: 'Knowledge Base Source', description: 'Choosing Uploaded Content means your AI Agent will respond only using the information you have added. If you choose OpenAI, all user queries will be answered directly by OpenAI.', side: 'bottom', align: 'start' } },
      { element: myContent[0], popover: { title: 'Knowledge Base Source', description: "Upload websites and documents here, your AI Agent will only use this content to answer queries when the knowledge base is set to Uploaded Content.", side: 'top', align: 'start' } },
      { element: unanswered[0], popover: { title: 'Unanswered Questions', description: "If your AI Agent can't find an answer in your uploaded content, the query will be handled based on the option you select here.", side: 'bottom', align: 'start' } },
      { element: saveButton[0], popover: { title: 'Save Reminder', description: 'After making any changes, always remember to save them.', side: 'top', align: 'end' } },
      { element: hhTab[0], popover: { title: 'Next Step', description: 'Use the Human Handover tab to define how chats are transferred to your team members.', side: 'bottom', align: 'start' } }
    ]
    driverObj = driverFn({
      showProgress: false, showButtons: ['next', 'previous'], allowClose: false, disableActiveInteraction: true,
      nextBtnText: 'Next', prevBtnText: 'Previous', doneBtnText: 'Finish', overlayColor: 'rgba(0, 0, 0, 0.6)',
      stagePadding: 8, stageRadius: 8, popoverClass: 'profile-tab-guide-popover', steps, popoverOffset: 15,
      onNextClick() { const next = driverObj.getActiveIndex() + 1; if (next >= steps.length) { saveStep(-1); driverObj.destroy() } else { currentStep = next; saveStep(currentStep); driverObj.moveNext() } },
      onPrevClick() { const prev = driverObj.getActiveIndex() - 1; if (prev >= 0) { currentStep = prev; saveStep(currentStep); driverObj.movePrevious() } },
      onPopoverRender: self.createPopoverRenderer(() => driverObj, steps)
    })
    if (currentStep > 0 && currentStep < steps.length) driverObj.drive(currentStep); else driverObj.drive()
  }

  initHumanHandoverTabGuide() {
    this.createTabGuide({
      tabName: 'human_handover', maxSteps: 4,
      getSteps() {
        const notify = $('#notify-user-on-agent-assignment').first(), assign = $('.assign-agent-strategy-control-group').first(), selectAgents = $('#select-agents-for-human-handover').first(), ifTab = $('#tab-intake-form')
        if (!notify.length || !assign.length || !selectAgents.length || !ifTab.length) return null
        return [
          { element: notify[0], popover: { title: 'Human Handover', description: 'Send a message to user when agent assigned to chat.', side: 'bottom', align: 'start' } },
          { element: assign[0], popover: { title: 'Agent Assignment Rules', description: 'Choose whether chats are assigned only to online agents or to both online and offline agents, and customize the messages shown to users.', side: 'top', align: 'start' } },
          { element: selectAgents[0], popover: { title: 'Agent Assignment Rules', description: 'Select which agents can receive chats. These agents must be created on the Agents page, and they will receive conversations on the Agent Desk page.', side: 'top', align: 'start' } },
          { element: ifTab[0], popover: { title: 'Next Steps', description: 'Use the Intake Form tab to collect user details before the conversation begins.', side: 'bottom', align: 'start' } }
        ]
      }
    }).init()
  }

  initIntakeFormTabGuide() {
    this.createTabGuide({
      tabName: 'intake_form', maxSteps: 4,
      getSteps() {
        const questions = $('#intake-form-questions-list-control-group').first(), greeting = $('#intake-form-greeting-and-ending-message-control-group').first(), summary = $('#intake-form-enable-summary-control-group').first(), instrTab = $('#tab-instructions')
        if (!questions.length || !greeting.length || !summary.length || !instrTab.length) return null
        return [
          { element: questions[0], popover: { title: 'Manage Questions', description: 'Add questions here to be asked by your AI Agent in a conversational manner to gather all responses at the start of the chat.', side: 'top', align: 'start' } },
          { element: greeting[0], popover: { title: 'Greeting and Ending Messages', description: 'Customize how your AI Agent greets users and ends the intake process.', side: 'bottom', align: 'start' } },
          { element: summary[0], popover: { title: 'Post-Intake Actions', description: 'Choose what happens after intake — send a summary in the chat, trigger a webhook, or transfer the user to a human agent.', side: 'top', align: 'start' } },
          { element: instrTab[0], popover: { title: 'Next Steps', description: "Use the Instructions tab to guide your AI Agent's behavior and tone during conversations.", side: 'bottom', align: 'start' } }
        ]
      }
    }).init()
  }

  initInstructionsTabGuide() {
    this.createTabGuide({
      tabName: 'instructions', maxSteps: 2,
      getSteps() {
        const sysMsgSection = $('#system-message-control-group').first(), analyticsTab = $('#tab-analytics')
        if (!sysMsgSection.length || !analyticsTab.length) return null
        return [
          { element: sysMsgSection[0], popover: { title: 'Instruction Tab', description: "Customize your AI Agent's behavior and response style here.\n\nKnowledge Base Instructions apply to responses generated from uploaded content.\n\nOpenAI Instructions are used when OpenAI is enabled or when queries fall outside your content.", side: 'bottom', align: 'start' } },
          { element: analyticsTab[0], popover: { title: 'Next Steps', description: "Use the Analytics tab to monitor your AI Agent's performance and user interactions.", side: 'bottom', align: 'start' } }
        ]
      }
    }).init()
  }

  initAnalyticsTabGuide() {
    this.createTabGuide({
      tabName: 'analytics', maxSteps: 3,
      getSteps() {
        const analytics = $('#analytics-control-group').first(), unanswered = $('#unanswered-questions-control-group').first(), widgetTab = $('#tab-widget')
        if (!analytics.length || !unanswered.length || !widgetTab.length) return null
        return [
          { element: analytics[0], popover: { title: 'Monthly Performance', description: 'View how many questions your AI Agent received each month and how many went unanswered.', side: 'bottom', align: 'start' } },
          { element: unanswered[0], popover: { title: 'Monthly Performance', description: "Generate and download the report to review unanswered questions and your AI Agent's responses, then update your Knowledge Base to help it answer these questions next time.", side: 'bottom', align: 'start' } },
          { element: widgetTab[0], popover: { title: 'Next Step', description: 'Use the Widget tab to create and customize how your AI Agent appears inside your website or application.', side: 'bottom', align: 'start' } }
        ]
      }
    }).init()
  }

  initWidgetTabGuide() {
    this.createTabGuide({
      tabName: 'widget', maxSteps: 5,
      getSteps() {
        const customize = $('#widget-customization-control-group').first(), addToPage = $('#add-to-display-within-a-webpage-control-group').first(), settings = $('#settings-control-group').first(), security = $('#security-control-group').first(), triggersTab = $('#tab-triggers')
        if (!customize.length || !addToPage.length || !settings.length || !security.length || !triggersTab.length) return null
        return [
          { element: customize[0], popover: { title: 'Customize Appearance', description: "Create your widget's name, image, and colors to match your brand.", side: 'top', align: 'start' } },
          { element: addToPage[0], popover: { title: 'Add to Your Website', description: 'Use these code snippets to embed your AI Agent on your website.', side: 'bottom', align: 'start' } },
          { element: settings[0], popover: { title: 'Widget Features', description: 'Enable or disable features like welcome messages, attachments, or text-to-speech for your users.', side: 'top', align: 'start' } },
          { element: security[0], popover: { title: 'Security', description: 'Restrict your widget to load only on a specific domain for added security.', side: 'top', align: 'start' } },
          { element: triggersTab[0], popover: { title: 'About Triggers', description: 'Use the Triggers tab to automate actions based on user messages or events.', side: 'bottom', align: 'start' } }
        ]
      }
    }).init()
  }

  initTriggersTabGuide() {
    if (!page_args.interactive_guide_state_path || !page_args.update_interactive_guide_path || page_args.tab_name !== 'triggers') return
    const self = this, gu = this.guideUtils
    gu.fetchGuideState().then(data => {
      if (data.guide_skipped || (data.tab_guides_skipped && data.tab_guides_skipped.triggers)) return
      const triggersProgress = data.tab_guides_progress && data.tab_guides_progress.triggers
      if (triggersProgress !== undefined && triggersProgress !== null && triggersProgress >= 1) return
      if ($('#triggers-list').length > 0 && $('#triggers-list li.trigger-item').length > 0) return
      setTimeout(() => self.startTriggersEmptyGuide(), 500)
    }).catch(e => console.error('Failed to load guide state', e))
  }

  startTriggersEmptyGuide() {
    const driverFn = this.guideUtils.getDriverFn(); if (!driverFn) return
    const self = this, gu = this.guideUtils
    this.ensureRestartButton()
    const addButton = $('.add-trigger-btn').first(); if (!addButton.length) return
    const steps = [{ element: addButton[0], popover: { title: 'About Triggers', description: 'Triggers allow you to create automated actions that run when specific conditions in the chat are met. You can create a trigger by clicking the Add button.', side: 'bottom', align: 'start' } }]
    const driverObj = driverFn({
      showProgress: false, showButtons: ['next'], allowClose: false, disableActiveInteraction: true, nextBtnText: 'Finish',
      overlayColor: 'rgba(0, 0, 0, 0.6)', stagePadding: 8, stageRadius: 8, popoverClass: 'profile-tab-guide-popover', steps, popoverOffset: 15,
      onNextClick() { gu.saveStep('triggers', 1).then(() => driverObj.destroy()).catch(() => driverObj.destroy()) },
      onPopoverRender: self.createPopoverRenderer(() => driverObj, steps)
    })
    driverObj.drive()
  }

  // ---- Restart button & completion popup ----

  ensureRestartButton() {
    const button = $('#guide-restart-button')
    if (button.length === 0) return
    button.css({ display: 'flex', 'z-index': '1100000003', visibility: 'visible', opacity: '1' })
  }

  initGuideRestartButton() {
    const self = this, button = $('#guide-restart-button')
    if (button.length === 0) return
    if (!button.data('click-bound')) {
      button.on('click', function (e) { e.preventDefault(); e.stopPropagation(); self.restartGuideFromEditPage() })
      button.data('click-bound', true)
    }
  }

  restartGuideFromEditPage() {
    if (!page_args.update_interactive_guide_path) return
    fetch(page_args.update_interactive_guide_path, {
      method: 'POST', headers: this.guideUtils.getHeaders(),
      body: JSON.stringify({ guide_type: 'reset_all' })
    }).then(() => window.location.reload())
  }

  showGuideCompletionPopup() {
    $('#guide-completion-popup').remove()
    $('.driver-overlay, .driver-popover, .driverjs-popover').remove()

    this.ensureRestartButton()
    const restartButton = document.getElementById('guide-restart-button')
    if (!restartButton) {
      console.error('Restart button not found')
      return
    }

    $('body').append(
      '<div id="guide-completion-popup" class="guide-completion-popup">' +
      '<div class="guide-completion-content">' +
      "<h4>You're all set!</h4>" +
      '<p>You can revisit this guide anytime by pressing the question mark icon</p>' +
      '<button class="guide-completion-ok-btn">Ok</button>' +
      '</div></div>'
    )

    const hideDriverChrome = () => {
      const overlay = document.querySelector('.driver-overlay')
      if (overlay) overlay.style.pointerEvents = 'none'
      const popover = document.querySelector('.driver-popover, .driverjs-popover')
      if (popover) popover.style.display = 'none'
    }

    const driverFn = this.guideUtils.getDriverFn()
    let completionDriver = null
    if (driverFn) {
      completionDriver = driverFn({
        showProgress: false,
        showButtons: [],
        allowClose: false,
        overlayColor: 'rgba(0, 0, 0, 0.5)',
        stagePadding: 4,
        stageRadius: 4,
        allowKeyboardControl: false,
        smoothScroll: false,
        disableActiveInteraction: false,
        popoverOffset: 15,
        steps: [{
          element: '#guide-restart-button',
          popover: { title: '', description: '', side: 'left', align: 'end' }
        }],
        onHighlightStarted() { setTimeout(hideDriverChrome, 50) },
        onHighlighted: hideDriverChrome,
        onDestroyed() { $('#guide-completion-popup').fadeOut(200, function () { $(this).remove() }) }
      })
      completionDriver.drive()
    }
    $(document).off('click', '#guide-completion-popup .guide-completion-ok-btn').on('click', '#guide-completion-popup .guide-completion-ok-btn', function (e) {
      e.preventDefault(); e.stopPropagation()
      if (completionDriver) completionDriver.destroy()
      else $('#guide-completion-popup').fadeOut(200, function () { $(this).remove() })
    })
  }
}
